'use client'

import { debounce } from 'lodash'
import { useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { determineFilterType } from '../components/filter-sections/filter-config'

// Types
export interface FilterState {
  [key: string]: string[] | [number, number]
}

interface UseFilterStateOptions {
  aggregations: IAggregations[]
  onLoadingChange?: (loading: boolean) => void
  onFilterApplied?: () => void
  debounceMs?: number
}

interface UseFilterStateReturn {
  filters: FilterState
  initialFilters: FilterState
  handleFilterChange: (attributeCode: string, value: string) => void
  handlePriceRangeChange: (attributeCode: string, newValue: number | number[]) => void
  handlePriceInputChange: (attributeCode: string, type: 'min' | 'max', value: string) => void
  handleReset: () => void
  applyFilters: (currentFilters: FilterState) => void
}

// Utils - Parse filter values từ URL
const parseFiltersFromURL = (
  searchParams: URLSearchParams,
  aggregations: IAggregations[],
  initialFilters: FilterState
): FilterState => {
  const newFilters: FilterState = { ...initialFilters }

  aggregations.forEach((agg) => {
    const filterType = determineFilterType(agg.attribute_code, agg.label)

    if (filterType === 'price') {
      const priceParam = searchParams.get('price')
      const defaultRange = initialFilters[agg.attribute_code] as [number, number]

      if (priceParam && priceParam.includes('-')) {
        const [min, max] = priceParam.split('-').map(Number)
        newFilters[agg.attribute_code] = [min, max]
      } else {
        newFilters[agg.attribute_code] = defaultRange
      }
    } else {
      const paramValue = searchParams.get(agg.attribute_code)
      newFilters[agg.attribute_code] = paramValue?.split(',').filter(Boolean) || []
    }
  })

  return newFilters
}

// Utils - Build URL params từ filter state
const buildURLParams = (
  currentFilters: FilterState,
  aggregations: IAggregations[],
  initialFilters: FilterState,
  searchParams: URLSearchParams
): URLSearchParams => {
  const params = new URLSearchParams(searchParams.toString())

  aggregations.forEach((agg) => {
    params.delete(agg.attribute_code)

    const filterValue = currentFilters[agg.attribute_code]
    const filterType = determineFilterType(agg.attribute_code, agg.label)

    if (filterType === 'price') {
      const [min, max] = filterValue as [number, number]
      const defaultRange = initialFilters[agg.attribute_code] as [number, number]

      // Chỉ set param nếu khác default
      if (min !== defaultRange[0] || max !== defaultRange[1]) {
        params.set('price', `${min}-${max}`)
      }
    } else {
      const values = filterValue as string[]
      if (values.length > 0) {
        params.set(agg.attribute_code, values.join(','))
      }
    }
  })

  params.set('page', '1')
  return params
}

// Utils - Tính toán initial filters từ aggregations
const computeInitialFilters = (aggregations: IAggregations[]): FilterState => {
  const filters: FilterState = {}

  aggregations.forEach((agg) => {
    const filterType = determineFilterType(agg.attribute_code, agg.label)

    if (filterType === 'price' && agg.options && agg.options.length > 0) {
      // Parse min-max từ price options
      let min = Infinity
      let max = -Infinity

      agg.options.forEach((option: AggregationsOption) => {
        const [minVal, maxVal] = option.value.split('_').map(Number)
        if (minVal < min) min = minVal
        if (maxVal > max) max = maxVal
      })

      filters[agg.attribute_code] = [min === Infinity ? 0 : min, max === -Infinity ? 0 : max]
    } else {
      filters[agg.attribute_code] = []
    }
  })

  return filters
}

// Helper - Check nếu đang ở Desktop
const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 900

/**
 * Custom hook quản lý toàn bộ filter state
 * - URL là source of truth
 * - Chỉ có 1 effect để cleanup debounce
 */
export const useFilterState = ({
  aggregations,
  onLoadingChange,
  onFilterApplied,
  debounceMs = 500,
}: UseFilterStateOptions): UseFilterStateReturn => {
  const searchParams = useSearchParams()

  // Memoize initial filters từ aggregations
  const initialFilters = useMemo(() => computeInitialFilters(aggregations), [aggregations])

  // Memoize filters từ URL (URL là source of truth)
  const filters = useMemo(
    () => parseFiltersFromURL(searchParams, aggregations, initialFilters),
    [searchParams, aggregations, initialFilters]
  )

  // Ref để track debounce function
  const debouncedApplyRef = useRef<ReturnType<typeof debounce> | null>(null)

  // Apply filters - push lên URL
  const applyFilters = useCallback(
    (currentFilters: FilterState) => {
      onFilterApplied?.()
      const params = buildURLParams(currentFilters, aggregations, initialFilters, searchParams)
      window.history.pushState({}, '', `?${params.toString()}`)
      onLoadingChange?.(false)
    },
    [aggregations, initialFilters, searchParams, onFilterApplied, onLoadingChange]
  )

  // Debounced apply - chỉ tạo 1 lần
  const debouncedApply = useMemo(() => {
    // Cancel debounce cũ nếu có
    debouncedApplyRef.current?.cancel()

    const debouncedFn = debounce((nextFilters: FilterState) => {
      applyFilters(nextFilters)
    }, debounceMs)

    debouncedApplyRef.current = debouncedFn
    return debouncedFn
  }, [applyFilters, debounceMs])

  // Cleanup debounce khi unmount
  useEffect(() => {
    return () => {
      debouncedApplyRef.current?.cancel()
    }
  }, [])

  // Handle checkbox/button filter change
  const handleFilterChange = useCallback(
    (attributeCode: string, value: string) => {
      const currentValues = filters[attributeCode] as string[]
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]

      const newFilters = { ...filters, [attributeCode]: newValues }

      if (isDesktop()) {
        onLoadingChange?.(true)
        debouncedApply(newFilters)
      } else {
        // Mobile: Chỉ update local, không apply ngay
        // Sẽ apply khi user click "Áp dụng"
      }
    },
    [filters, debouncedApply, onLoadingChange]
  )

  // Handle price range slider change
  const handlePriceRangeChange = useCallback(
    (attributeCode: string, newValue: number | number[]) => {
      const newFilters = {
        ...filters,
        [attributeCode]: newValue as [number, number],
      }

      if (isDesktop()) {
        onLoadingChange?.(true)
        debouncedApply(newFilters)
      }
    },
    [filters, debouncedApply, onLoadingChange]
  )

  // Handle price input change
  const handlePriceInputChange = useCallback(
    (attributeCode: string, type: 'min' | 'max', value: string) => {
      const numValue = Number(value.replace(/\D/g, ''))
      const currentRange = filters[attributeCode] as [number, number]
      const newRange: [number, number] =
        type === 'min' ? [numValue, currentRange[1]] : [currentRange[0], numValue]

      const newFilters = { ...filters, [attributeCode]: newRange }

      if (isDesktop()) {
        onLoadingChange?.(true)
        debouncedApply(newFilters)
      }
    },
    [filters, debouncedApply, onLoadingChange]
  )

  // Reset filters về default
  const handleReset = useCallback(() => {
    applyFilters(initialFilters)
  }, [applyFilters, initialFilters])

  return {
    filters,
    initialFilters,
    handleFilterChange,
    handlePriceRangeChange,
    handlePriceInputChange,
    handleReset,
    applyFilters,
  }
}
